<template>
  <div>
    <simple-modal :id="modalId" :step-id="stepId" :key="stepId" @close-modal="onCloseClick">
      <div id="modal-fullscreen" role="dialog" class="step-modal">
        <form :id="'form' + stepId" onsubmit="return false;">
          <div class="modal-dialog simple-modal-dialog" v-cloak>
            <div class="modal-header">
              <h1 class="modal-title">{{ stepName }}</h1>
              <span v-html="stepDescription"></span>
            </div>
            <div class="modal-body">
              <div v-if="!piecesInitialized" class="pieces-loading-wrapper">
                <div class="loading-indicator"></div>
              </div>
              <div class="form-horizontal wizard" :class="{hide: !piecesInitialized}">
                <input type="hidden" name="id" :value="stepId" />

                <step-pieces
                  :pieces="pieces"
                  :campaign-id="campaign.id"
                  :advanced-email-options="advancedEmailOptions"
                  :step-id="stepId"
                  :step-activity-type="stepActivityType"
                  :first-step-id="firstStepId"
                  @disable-saving="onDisableSaving($event)"
                ></step-pieces>
                <step-advanced-settings-modal
                  v-if="advancedSettingsEnabled"
                  :campaign-id="campaign.id"
                  :pieces="pieces"
                  :step-id="stepId"
                  :show-form-submission-notifications="showFormSubmissionNotifications"
                  :form-submission-notifications-title="formSubmissionNotificationsTitle"
                  :form-submission-notifications-description="formSubmissionNotificationsDescription"
                  :emailable-type="'steps'"
                  :emailable-id="stepId"
                ></step-advanced-settings-modal>
              </div>
            </div>
          </div>
          <div class="sk-actions">
            <div class="pull-left" style="margin-top: 6px">
              <b>Status: </b>
              <select
                :id="'status' + stepId"
                name="status"
                style="height: 30px !important; position: relative; top: 1px"
                class="mr-3"
              >
                <option value="true" :selected="stepStatus === true">Enabled</option>
                <option value="false" v-if="stepActivityType != 'documents'" :selected="stepStatus === false">
                  Disabled
                </option>
              </select>
              <button
                v-if="advancedSettingsEnabled"
                class="btn btn-default mr-3 advancedSettingsButton"
                type="button"
                data-toggle="modal"
                :data-target="'#step-advanced-settings-modal-' + stepId"
              >
                Advanced Settings
              </button>
            </div>
            <span class="exit-step-button btn-lg" @click="onCloseClick">Exit</span> &nbsp;&nbsp;
            <span class="d-inline-block" v-tooltip="saveButtonTitle">
              <button
                type="submit"
                :class="'button btn-lg btn-primary'"
                data-test="step-save-button"
                :disabled="savingDisabled"
                @click="handleSaveStep()"
              >
                Save
              </button>
            </span>
          </div>
        </form>
      </div>
    </simple-modal>
    <box-content-picker-modal
      :id="boxModalId"
      :shown="boxModalShown"
      @hide="onHideBoxModal"
      @select-files="onSelectBoxFiles($event)"
    ></box-content-picker-modal>
  </div>
</template>

<script>
import buildFormParams from '../mixins/formParamBuilder';
import SimpleModal from '../components/SimpleModal';
import BoxContentPickerModal from '../components/box/BoxContentPickerModal';
import StepAdvancedSettingsModal from './campaign/edit/advanced/StepAdvancedSettingsModal';
import StepPieces from './campaign/edit/StepPieces';
import swal from 'sweetalert2';
import {getCampaignStepPieces} from '../utility/api/campaign/getCampaignStepPieces';
import {updateCampaignStep} from '../utility/api/campaign/updateCampaignStep';
import {uploadGalleryImage} from '../utility/gallery/uploadGalleryImage';
import {validateGalleryImage} from '../utility/gallery/validateGalleryImage';
import {removeBlobImageFromActiveTinyMceEditor} from '../utility/tinymce/removeBlobImageFromActiveTinyMceEditor';
import {stickyToolbarTinyMcePlugin} from '../utility/tinymce/stickyToolbarTinyMcePlugin';
import {formatErrorResponse} from '../utility/api/formatErrorResponse';
import {storeSubscribingCampaigns} from '../utility/api/campaign/storeSubscribingCampaigns';

const TINY_MCE_PLUGINS = 'autoresize link code media lists quickbars image imagetools stickytoolbar paste';

export default {
  name: 'edit-step',
  components: {
    BoxContentPickerModal,
    SimpleModal,
    StepAdvancedSettingsModal,
    StepPieces,
  },
  mixins: [buildFormParams],
  methods: {
    createTinyMceToolbar(fullpage, media) {
      return `undo redo | styleselect | bold italic forecolor backcolor | lineheight | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link ${
        media ? ' | media' : ''
      }| quickimage editimage imageoptions | box | ${fullpage ? 'fullpage ' : ''}code`;
    },
    initTinyMCE(fullpage = false) {
      tinymce.init({
        selector: `#${this.modalId} textarea.tinymcearea${fullpage ? '-fullpage' : ''}`,
        menubar: false,
        statusbar: false,
        max_height: 1500,
        plugins: (fullpage ? 'fullpage ' : '') + TINY_MCE_PLUGINS,
        allow_conditional_comments: true,
        content_style: 'img {max-width: 100%;}',
        toolbar: this.createTinyMceToolbar(fullpage, this.stepActivityType === 'pages'),
        setup: editor => this.setupTinyMCE(editor),
        sticky_offset: this.adminBarShown ? 32 : 0,
        sticky_scrolling_container: `#${this.modalId}`,
        sticky_toolbar_container: '.tox-toolbar__primary',
        quickbars_image_toolbar: false,
        quickbars_insert_toolbar: false,
        quickbars_selection_toolbar: false,
        contextmenu: false,
        images_upload_handler: (blobInfo, success, failure, progress) =>
          this.handleTinyMceImageUpload(blobInfo, success, failure, progress),
      });
      tinymce.PluginManager.add('stickytoolbar', stickyToolbarTinyMcePlugin);
    },
    initTinyMCEFull() {
      this.initTinyMCE(true);
    },
    removeTinyMCE() {
      tinymce.remove(`#${this.modalId} textarea.tinymcearea`);
      tinymce.remove(`#${this.modalId} textarea.tinymcearea-fullpage`);
    },
    setupTinyMCE(editor) {
      // editor.ui.registry.addIcon('boxcom', `<svg class="boxcomButton" style="height:24px;width:24px" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:cc="http://creativecommons.org/ns#" xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#" xmlns:svg="http://www.w3.org/2000/svg" xmlns="http://www.w3.org/2000/svg" xmlns:sodipodi="http://sodipodi.sourceforge.net/DTD/sodipodi-0.dtd" xmlns:inkscape="http://www.inkscape.org/namespaces/inkscape" version="1.1" id="svg2" xml:space="preserve" width="613.56" height="324.93332" viewBox="0 0 613.56 324.93332" sodipodi:docname="box_black.eps"><metadata id="metadata8"><rdf:RDF><cc:Work rdf:about=""><dc:format>image/svg+xml</dc:format><dc:type rdf:resource="http://purl.org/dc/dcmitype/StillImage" /></cc:Work></rdf:RDF></metadata><defs id="defs6" /><sodipodi:namedview pagecolor="#ffffff" bordercolor="#666666" borderopacity="1" objecttolerance="10" gridtolerance="10" guidetolerance="10" inkscape:pageopacity="0" inkscape:pageshadow="2" inkscape:window-width="640" inkscape:window-height="480" id="namedview4" /><g id="g10" inkscape:groupmode="layer" inkscape:label="ink_ext_XXXXXX" transform="matrix(1.3333333,0,0,-1.3333333,0,324.93333)"><g id="g12" transform="scale(0.1)"><path d="m 2375.34,343.73 c -284.5,0 -515.18,230.469 -515.18,514.969 0,284.261 230.68,514.681 515.18,514.681 284.38,0 514.94,-230.42 514.94,-514.681 0,-284.5 -230.56,-514.969 -514.94,-514.969 m -1516.926,0 c -284.437,0 -515.285,230.411 -515.285,514.86 0,284.37 230.848,514.79 515.285,514.79 284.406,0 514.766,-230.42 514.766,-514.681 0,-284.5 -230.36,-514.969 -514.766,-514.969 M 2375.34,1717.09 c -328.53,0 -614.42,-184.79 -758.4,-456.11 -144.02,271.32 -429.77,456.11 -758.526,456.11 -193.047,0 -371.387,-63.85 -515.285,-171.71 v 723.3 c -1.699,93.46 -77.918,168.28 -171.715,168.28 C 77.6289,2436.96 2.14844,2362.14 0,2268.68 V 844.391 H 0.191406 C 7.75391,376.73 388.844,0 858.414,0 1187.17,0 1472.92,184.961 1616.94,456.02 1760.92,184.961 2046.81,0 2375.34,0 c 473.99,0 858.5,384.359 858.5,858.699 0,474.121 -384.51,858.391 -858.5,858.391" style="fill:#201c1c;fill-opacity:1;fill-rule:nonzero;stroke:none" id="path14" /><path d="m 4566,287.422 -466.66,572.398 467.23,573.51 c 59.06,75.66 42.13,181.29 -39.42,237.42 -81.65,56.6 -196.2,42.09 -259.38,-31.32 v 0.12 l -401.96,-492.64 -401.62,492.64 v -0.12 c -62.52,73.41 -177.79,87.92 -259.19,31.32 -81.4,-56.18 -98.41,-161.76 -39.05,-237.42 h -0.16 L 3632.17,859.82 3165.79,287.422 h 0.16 C 3106.59,211.551 3123.6,106.219 3205,49.8711 3286.4,-6.5 3401.67,7.85156 3464.19,81.3711 L 3865.81,573.328 4267.2,81.3711 c 63.24,-73.51954 177.77,-87.8711 259.41,-31.5 81.56,56.3479 98.57,161.6799 39.39,237.5509" style="fill:#201c1c;fill-opacity:1;fill-rule:nonzero;stroke:none" id="path16" /></g></g></svg>`);
      // editor.ui.registry.addButton('box', {
      //     icon: 'boxcom',
      //     tooltip: 'Box.com',
      //     onAction: () => {
      //         editor.focus();
      //         this.boxModalShown = true;
      //     }
      // });
    },
    async handleTinyMceImageUpload(blobInfo, success, failure, progress) {
      try {
        await validateGalleryImage(blobInfo.blob());
        await this.uploadImageToAwsS3(blobInfo, success, failure, progress);
      } catch (error) {
        failure(error.message || 'A server error occurred.');
        removeBlobImageFromActiveTinyMceEditor();
      }
    },
    async uploadImageToAwsS3(blobInfo, success, failure, progress) {
      this.disableSavingWhenUploadingImage(true);

      try {
        const file = new File([blobInfo.blob()], blobInfo.filename());
        const url = await uploadGalleryImage(file, progress);
        success(url);
      } finally {
        this.disableSavingWhenUploadingImage(false);
      }
    },
    disableSavingWhenUploadingImage(disabled) {
      this.onDisableSaving({disabled, pieceId: 'image-upload', message: 'Image has not been uploaded yet!'});
    },
    handleSaveStep() {
      this.$emit('close-modal');
      tinyMCE.triggerSave();

      let statusDrop = document.getElementById('status' + this.stepId);
      let stepStatus = !!statusDrop && !!statusDrop.options ? statusDrop.options[statusDrop.selectedIndex].value : '';
      document.getElementById('timeline-panel' + this.stepId).setAttribute('data-status', stepStatus);

      const formData = Object.entries(this.buildFormParams('form' + this.stepId)).reduce(
        (formData, [key, value]) => (formData.append(key, value), formData),
        new FormData()
      );


      let subscribing_campaigns = formData.get('subscribing_campaigns');
      formData.delete('subscribing_campaigns');

      updateCampaignStep(this.campaign.id, formData)
        .then(() => {
          if (subscribing_campaigns) {
            subscribing_campaigns = subscribing_campaigns.split(',');

            storeSubscribingCampaigns(this.campaign.id, this.stepId, {
              campaign_ids: subscribing_campaigns,
            });
          }

          $.notify('Saved!', 'success');
          this.$emit('step-completed');
        })
        .catch(error => {
          const text =
            error.response && error.response.data
              ? formatErrorResponse(error.response.data)
              : 'There was an error saving the step.';
          swal.fire({
            title: 'UhOh!',
            text,
            type: 'error',
            confirmButtonText: 'Ok',
            buttonsStyling: false,
            confirmButtonClass: 'btn btn-lg btn-primary',
          });
        });
    },
    refreshTextAreas() {
      let els = document.querySelectorAll('.step-messagearea');

      for (let i = 0; i < els.length; i++) {
        let height = els[i].scrollHeight;

        els[i].style.height = height + 'px';
      }

      let mceEls = document.querySelectorAll('.tox.tox-tinymce'),
        resizeByEls = document.querySelectorAll('.tox-edit-area__iframe');

      for (let z = 0; z < mceEls.length; z++) {
        let height = resizeByEls[z].scrollHeight;

        mceEls[z].style.height = height + 'px';
      }
    },
    openModal() {
      document.getElementById(this.modalId).style.display = 'block';
      document.body.style.overflow = 'hidden';
      this.piecesInitialized = false;

      getCampaignStepPieces(this.campaign.id, this.stepId).then(responseData => {
        this.pieces = responseData.data;

        this.$nextTick(() => {
          this.initTinyMCE();
          this.initTinyMCEFull();
          this.piecesInitialized = true;

          this.$nextTick(() => {
            this.refreshTextAreas();
          });
        });
      });
    },
    closeModal() {
      this.removeTinyMCE();

      let element = document.getElementById(this.modalId);

      if (element) {
        element.style.display = 'none';
      }

      document.body.style.overflow = null;
      this.boxModalShown = false;
    },
    onCloseClick() {
      this.$emit('close-modal');
    },
    onDisableSaving(event) {
      this.savingDisabledMap = {...this.savingDisabledMap, [event.pieceId]: event};
    },
    onHideBoxModal() {
      this.boxModalShown = false;
    },
    onSelectBoxFiles(files) {
      const links = (files || []).map(({name, url}) => `<a href="${url}" target="_blank">${name}</a>`);
      tinymce.activeEditor.insertContent(links.join('<br/>'));
    },
  },
  watch: {
    campaign: {
      immediate: true,
      handler(campaign) {
        if (campaign && campaign.lockChangedCampaigns && campaign.active) {
          this.onDisableSaving({
            disabled: true,
            pieceId: 'campaign-locked',
            message: 'Editing is disabled since this campaign has been launched.',
          });
        }
      },
    },
    modalShown(shown) {
      if (shown) {
        this.openModal();
      } else {
        this.closeModal();
      }
    },
  },
  data() {
    return {
      boxModalShown: false,
      pieces: [],
      piecesInitialized: false,
      savingDisabledMap: {},
    };
  },
  computed: {
    boxModalId() {
      return `box-content-picker-modal-${this.stepId}`;
    },
    saveButtonTitle() {
      const record = Object.values(this.savingDisabledMap).find(r => r.disabled);
      return record && record.message;
    },
    savingDisabled() {
      return Object.values(this.savingDisabledMap).some(({disabled}) => disabled);
    },
    advancedSettingsEnabled() {
      return this.stepActivityType === 'pages';
    },
  },
  props: {
    adminBarShown: {
      type: Boolean,
      required: false,
    },
    campaign: {
      type: Object,
      required: true,
    },
    advancedEmailOptions: {
      type: Boolean,
      required: false,
    },
    stepId: {
      type: Number,
      required: true,
    },
    stepIndex: {
      type: Number,
      required: true,
    },
    stepName: {
      type: String,
      required: true,
    },
    stepStatus: {
      type: Boolean,
      required: true,
    },
    stepDescription: {
      type: String,
      required: true,
    },
    stepActivityType: {
      type: String,
      required: true,
    },
    modalId: {
      type: String,
      required: true,
    },
    modalShown: {
      type: Boolean,
      required: true,
    },
    firstStepId: {
      type: Number,
      required: true,
    },
    showFormSubmissionNotifications: {
      type: Boolean,
      required: true,
    },
    formSubmissionNotificationsTitle: {
      type: String,
      required: true,
    },
    formSubmissionNotificationsDescription: {
      type: String,
      required: true,
    },
  },
};
</script>
<style lang="scss">
.modal-title {
  visibility: visible;
}

.hidePieces {
  display: none;
}

.tox-collection__item-label > h1 {
  visibility: visible;
  min-height: initial;
  letter-spacing: normal;
}

.tox-notifications-container {
  position: fixed !important;
  top: 32px !important;
}
</style>
